/**
 * Structure-of-arrays entity storage (KTD8).
 *
 * Hot per-entity fields are parallel typed arrays indexed by a dense slot.
 * Snapshotting is a buffer copy and hashing is a linear scan over contiguous
 * memory, both of which the determinism and desync machinery need constantly.
 *
 * **Iteration order is always ascending slot index.** Never iterate a Map or a
 * Set here — an accidental ordering dependency is a desync waiting to happen.
 *
 * Entity ids carry a generation counter alongside the slot. Without it a
 * projectile in flight toward a dead unit hits whatever recycled its slot, and
 * a stale selection silently commands a stranger's tank.
 */

import type { Ctx, Subsystem } from '../core/registry.js'
import type { Fixed } from '../core/fixed.js'
import { MAX_ENTITIES } from '../core/config.js'

/** Bit flags in the `flags` array. */
export const EF = {
  Alive: 1 << 0,
  Structure: 1 << 1,
  /** Can found a base by deploying — the R30 elimination test reads this. */
  Founder: 1 << 2,
  Harvester: 1 << 3,
  /** Selectable and orderable by its owner. */
  Mobile: 1 << 4,
  /** Currently offline for want of power. */
  Offline: 1 << 5,
  /** Under construction; not yet a legal prerequisite. */
  Building: 1 << 6,
  /** Flies: ignores terrain, reserves no cell, and nothing blocks it. */
  Air: 1 << 7,
  /** Being repaired: healing a little each tick and charging for it. */
  Repairing: 1 << 8,
  /**
   * Dug in: sandbagged in place, tougher, and unable to move until it packs
   * up again. Infantry only — see `Build.tryDigIn`.
   */
  DugIn: 1 << 9,
} as const

/** Unit behaviour state. Kept small and integral so it lives in a Uint8Array. */
export const ST = {
  Idle: 0,
  Moving: 1,
  AttackMoving: 2,
  Attacking: 3,
  Guarding: 4,
  Seeking: 5,
  Mining: 6,
  Returning: 7,
  Unloading: 8,
  Scattering: 9,
  Dead: 10,
  /** Aircraft flying home with an empty magazine. */
  RearmingInbound: 11,
  /** Aircraft parked at its airbase, refilling. */
  Rearming: 12,
} as const

export const NO_ENTITY = 0

/** Encode a slot and generation into a stable id. Generation starts at 1. */
export function makeId(slot: number, generation: number): number {
  return ((generation << 16) | (slot & 0xffff)) >>> 0
}

export function slotOf(id: number): number {
  return id & 0xffff
}

export function generationOf(id: number): number {
  return (id >>> 16) & 0xffff
}

export interface SpawnParams {
  kind: number
  owner: number
  x: Fixed
  y: Fixed
  hp: number
  flags?: number
  heading?: number
  /** Starting ordnance, for aircraft. */
  ammo?: number
}

export class Entities implements Subsystem {
  static id = 'entities'

  readonly capacity: number

  // --- hot fields, all parallel and slot-indexed ---------------------------
  readonly kind: Uint16Array
  readonly generation: Uint16Array
  readonly flags: Uint16Array
  readonly owner: Int8Array
  readonly state: Uint8Array

  readonly x: Int32Array
  readonly y: Int32Array
  readonly vx: Int32Array
  readonly vy: Int32Array
  readonly heading: Uint16Array
  readonly turret: Uint16Array

  readonly hp: Int32Array
  readonly maxHp: Int32Array
  readonly cooldown: Int32Array
  readonly target: Uint32Array

  /** Order destination in world space. */
  readonly orderX: Int32Array
  readonly orderY: Int32Array
  /** Harvester load, or build progress for a structure under construction. */
  readonly cargo: Int32Array
  /**
   * Credits the current load is worth.
   *
   * Separate from `cargo`, which is volume and decides when a harvester is
   * full. Gems fill the same bucket at the same rate and are worth several
   * times as much, so value cannot be derived from volume at unload time —
   * and by then the cell has often been mined out and reset to plain ground,
   * so there is nothing left to look it up from either.
   */
  readonly cargoValue: Int32Array
  /**
   * The entity *kind* riding inside this one, or 0.
   *
   * The kind rather than the entity: a passenger has no position, no health of
   * its own while carried and nothing to simulate, so keeping a live entity
   * parked off the map would be a second thing to keep in step for no benefit.
   * It is destroyed on boarding and rebuilt on unloading, which is also why an
   * IFV losing its passenger to a lucky shell is not a thing that can happen —
   * the passenger shares the vehicle's fate, as it should.
   */
  readonly passenger: Int32Array
  /** Ticks spent in the current state — drives mining and unload durations. */
  readonly timer: Int32Array
  /** Cell this entity has claimed for its next step (U8 reservation). */
  readonly claim: Int32Array
  /** Shots an aircraft has left before it must land and rearm. */
  readonly ammo: Int32Array
  /**
   * Kills this unit has scored, and the rank it has earned.
   *
   * Both are simulation state and both are hashed: a veteran unit hits harder
   * and survives longer, so two clients disagreeing about a rank diverge on the
   * next shot it fires.
   */
  readonly kills: Int32Array
  readonly rank: Int32Array
  /** Ticks left of a rearm cycle. */
  readonly rearm: Int32Array

  /** Free slot stack. `freeTop` is the number of entries in use. */
  private readonly freeList: Int32Array
  private freeTop = 0
  /** Highest slot ever allocated, so iteration can stop early. */
  private highWater = 0
  private liveCount = 0

  /**
   * Infrequent or variable-shape per-entity data (KTD8's side table). Hashed by
   * walking keys in **ascending entity id**, never in Map insertion order.
   */
  private readonly side = new Map<number, Int32Array>()
  private sideHashBuf = new Int32Array(0)

  private ctx!: Ctx

  constructor(capacity = MAX_ENTITIES) {
    if (capacity > 0xffff) throw new Error('slot indices are 16-bit; capacity must be under 65536')
    this.capacity = capacity
    this.kind = new Uint16Array(capacity)
    this.generation = new Uint16Array(capacity)
    this.flags = new Uint16Array(capacity)
    this.owner = new Int8Array(capacity)
    this.state = new Uint8Array(capacity)
    this.x = new Int32Array(capacity)
    this.y = new Int32Array(capacity)
    this.vx = new Int32Array(capacity)
    this.vy = new Int32Array(capacity)
    this.heading = new Uint16Array(capacity)
    this.turret = new Uint16Array(capacity)
    this.hp = new Int32Array(capacity)
    this.maxHp = new Int32Array(capacity)
    this.cooldown = new Int32Array(capacity)
    this.target = new Uint32Array(capacity)
    this.orderX = new Int32Array(capacity)
    this.orderY = new Int32Array(capacity)
    this.cargo = new Int32Array(capacity)
    this.cargoValue = new Int32Array(capacity)
    this.passenger = new Int32Array(capacity)
    this.timer = new Int32Array(capacity)
    this.claim = new Int32Array(capacity).fill(-1)
    this.ammo = new Int32Array(capacity)
    this.kills = new Int32Array(capacity)
    this.rank = new Int32Array(capacity)
    this.rearm = new Int32Array(capacity)

    this.freeList = new Int32Array(capacity)
    this.owner.fill(-1)
  }

  init(ctx: Ctx): void {
    this.ctx = ctx
    // Every array is registered. A field that is not hashed is a field that can
    // diverge without the desync gate noticing.
    for (const [name, view] of this.hashRegions()) ctx.hash.register(name, view)
    ctx.hash.register('entities:freelist', () => this.freeList.subarray(0, this.freeTop))
    ctx.hash.register('entities:counters', () => {
      this.countersView[0] = this.freeTop
      this.countersView[1] = this.highWater
      this.countersView[2] = this.liveCount
      return this.countersView
    })
    ctx.hash.register('entities:side', () => this.sideHashView())
  }

  private readonly countersView = new Int32Array(3)

  private hashRegions(): Array<[string, ArrayBufferView]> {
    return [
      ['entities:ammo', this.ammo],
      ['entities:kills', this.kills],
      ['entities:rank', this.rank],
      ['entities:rearm', this.rearm],
      ['entities:kind', this.kind],
      ['entities:generation', this.generation],
      ['entities:flags', this.flags],
      ['entities:owner', this.owner],
      ['entities:state', this.state],
      ['entities:x', this.x],
      ['entities:y', this.y],
      ['entities:vx', this.vx],
      ['entities:vy', this.vy],
      ['entities:heading', this.heading],
      ['entities:turret', this.turret],
      ['entities:hp', this.hp],
      ['entities:maxHp', this.maxHp],
      ['entities:cooldown', this.cooldown],
      ['entities:target', this.target],
      ['entities:orderX', this.orderX],
      ['entities:orderY', this.orderY],
      ['entities:cargo', this.cargo],
      ['entities:cargoValue', this.cargoValue],
      ['entities:passenger', this.passenger],
      ['entities:timer', this.timer],
      ['entities:claim', this.claim],
    ]
  }

  get count(): number {
    return this.liveCount
  }

  /** One past the highest slot ever allocated. Iterate `[0, limit)`. */
  get limit(): number {
    return this.highWater
  }

  /**
   * Allocate an entity. Returns `NO_ENTITY` when at capacity — spawning past
   * capacity must fail cleanly rather than corrupt the arrays.
   */
  spawn(p: SpawnParams): number {
    let slot: number
    if (this.freeTop > 0) {
      slot = this.freeList[--this.freeTop]
    } else if (this.highWater < this.capacity) {
      slot = this.highWater++
    } else {
      return NO_ENTITY
    }

    // Generation advances on *allocation*, so a recycled slot never yields an
    // id equal to the one it had before.
    let gen = (this.generation[slot] + 1) & 0xffff
    if (gen === 0) gen = 1
    this.generation[slot] = gen

    this.kind[slot] = p.kind
    this.owner[slot] = p.owner
    this.flags[slot] = (p.flags ?? 0) | EF.Alive
    this.state[slot] = ST.Idle
    this.x[slot] = p.x
    this.y[slot] = p.y
    this.vx[slot] = 0
    this.vy[slot] = 0
    this.heading[slot] = p.heading ?? 0
    this.turret[slot] = p.heading ?? 0
    this.hp[slot] = p.hp
    this.maxHp[slot] = p.hp
    this.cooldown[slot] = 0
    this.target[slot] = NO_ENTITY
    this.orderX[slot] = p.x
    this.orderY[slot] = p.y
    this.cargo[slot] = 0
    this.cargoValue[slot] = 0
    this.passenger[slot] = 0
    this.timer[slot] = 0
    this.claim[slot] = -1
    this.ammo[slot] = p.ammo ?? 0
    this.kills[slot] = 0
    this.rank[slot] = 0
    this.rearm[slot] = 0
    this.liveCount++

    const id = makeId(slot, gen)
    this.ctx?.emit('actor:spawn', { id, kind: p.kind, owner: p.owner, x: p.x, y: p.y })
    return id
  }

  /** Remove an entity. Returns false if the id was already stale. */
  destroy(id: number): boolean {
    const slot = this.resolve(id)
    if (slot < 0) return false
    this.flags[slot] = 0
    this.state[slot] = ST.Dead
    this.owner[slot] = -1
    this.hp[slot] = 0
    this.claim[slot] = -1
    this.side.delete(id)
    this.freeList[this.freeTop++] = slot
    this.liveCount--
    // Subsystems holding per-slot derived state — the pathfinder's cell
    // occupancy above all — need to release it, and they cannot poll for it.
    this.ctx?.emit('actor:removed', { id, slot })
    return true
  }

  /**
   * Slot for a live id, or -1. This is the generation check, and it is the
   * reason a stale reference is detectable rather than silently aliasing.
   */
  resolve(id: number): number {
    if (id === NO_ENTITY) return -1
    const slot = id & 0xffff
    if (slot >= this.highWater) return -1
    if ((this.flags[slot] & EF.Alive) === 0) return -1
    if (this.generation[slot] !== ((id >>> 16) & 0xffff)) return -1
    return slot
  }

  isLive(id: number): boolean {
    return this.resolve(id) >= 0
  }

  /** Current id of a slot, or NO_ENTITY if the slot is free. */
  idAt(slot: number): number {
    if (slot < 0 || slot >= this.highWater) return NO_ENTITY
    if ((this.flags[slot] & EF.Alive) === 0) return NO_ENTITY
    return makeId(slot, this.generation[slot])
  }

  ownerOf(id: number): number {
    const slot = this.resolve(id)
    return slot < 0 ? -1 : this.owner[slot]
  }

  isAlive(slot: number): boolean {
    return (this.flags[slot] & EF.Alive) !== 0
  }

  has(slot: number, flag: number): boolean {
    return (this.flags[slot] & flag) !== 0
  }

  setFlag(slot: number, flag: number, on: boolean): void {
    if (on) this.flags[slot] |= flag
    else this.flags[slot] &= ~flag
  }

  /**
   * Live slots in ascending order. Allocation-free: the caller supplies the
   * callback and the loop never builds an array.
   */
  forEach(fn: (slot: number) => void): void {
    for (let s = 0; s < this.highWater; s++) {
      if ((this.flags[s] & EF.Alive) !== 0) fn(s)
    }
  }

  /** Live slots owned by `player`, ascending. */
  forEachOwned(player: number, fn: (slot: number) => void): void {
    for (let s = 0; s < this.highWater; s++) {
      if ((this.flags[s] & EF.Alive) !== 0 && this.owner[s] === player) fn(s)
    }
  }

  // --- side table ----------------------------------------------------------

  /** Attach variable-shape data to an entity. Keyed by id, not slot. */
  setSide(id: number, data: Int32Array): void {
    if (this.resolve(id) < 0) return
    this.side.set(id, data)
  }

  getSide(id: number): Int32Array | undefined {
    return this.side.get(id)
  }

  deleteSide(id: number): void {
    this.side.delete(id)
  }

  get sideCount(): number {
    return this.side.size
  }

  /**
   * Flatten the side table for hashing, walking keys in **ascending entity id**.
   * Map insertion order would make the hash depend on the order entities
   * happened to acquire side data, which differs between clients.
   */
  private sideHashView(): Int32Array {
    const keys: number[] = []
    for (const k of this.side.keys()) keys.push(k)
    keys.sort((a, b) => a - b)

    let need = 0
    for (const k of keys) need += 2 + (this.side.get(k)?.length ?? 0)
    if (this.sideHashBuf.length < need) this.sideHashBuf = new Int32Array(need * 2)

    let o = 0
    for (const k of keys) {
      const data = this.side.get(k)!
      this.sideHashBuf[o++] = k | 0
      this.sideHashBuf[o++] = data.length
      this.sideHashBuf.set(data, o)
      o += data.length
    }
    return this.sideHashBuf.subarray(0, o)
  }

  /** Wipe every slot. Used by scenario setup, never mid-match. */
  reset(): void {
    this.kind.fill(0)
    this.generation.fill(0)
    this.flags.fill(0)
    this.owner.fill(-1)
    this.state.fill(0)
    this.x.fill(0)
    this.y.fill(0)
    this.vx.fill(0)
    this.vy.fill(0)
    this.heading.fill(0)
    this.turret.fill(0)
    this.hp.fill(0)
    this.maxHp.fill(0)
    this.cooldown.fill(0)
    this.kills.fill(0)
    this.rank.fill(0)
    this.target.fill(0)
    this.orderX.fill(0)
    this.orderY.fill(0)
    this.cargo.fill(0)
    this.cargoValue.fill(0)
    this.passenger.fill(0)
    this.timer.fill(0)
    this.claim.fill(-1)
    this.freeList.fill(0)
    this.freeTop = 0
    this.highWater = 0
    this.liveCount = 0
    this.side.clear()
  }
}
